<script setup lang="ts">
import { useField, useFieldArray, useFormValues, useFieldError } from 'vee-validate'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { push, remove, replace, fields } = useFieldArray(
  'projekt.financovanie.statnaPomoc.schemyStatnejPomoci'
)
const formValues = useFormValues()
const specResponse = inject<any>('specResponse')
const schemyStatnejPomociData = specResponse.zoppmSpec.schemyStatnejPomoci
const { setValue: setStatnaPomoc } = useField(
  'projekt.financovanie.statnaPomoc',
  undefined,
  { initialValue: null }
)

const errorMessage = useFieldError('projekt.financovanie.statnaPomoc.schemyStatnejPomoci')

const RADIOS_OPTIONS = [
  { id: '1', value: 'true', label: t('zoppm.step11.part2.radioYes') },
  { id: '0', value: 'false', label: t('zoppm.step11.part2.radioNo') },
] as const

type RadiosValuesType = typeof RADIOS_OPTIONS[number]['value']

let schemyStatnejPomociCheckboxes: any[] = []
for (const item in schemyStatnejPomociData) {
  schemyStatnejPomociCheckboxes.push({
    id: schemyStatnejPomociData[item]?.id,
    label: schemyStatnejPomociData[item]?.nazov,
    value: schemyStatnejPomociData[item]?.kodZdroj,
  })
}

const checkboxValues = computed(
  () =>
    formValues.value?.projekt?.financovanie?.statnaPomoc?.schemyStatnejPomoci?.map(
      (schema: any) => schema.kodZdroj
    ) || []
)

const shouldBeExpanded = computed<RadiosValuesType>(() => {
  const result =
    checkboxValues?.value?.length > 0 || // Ak je zvolena aspon jedna schema statnej pomoci
    formValues.value?.projekt?.financovanie?.statnaPomoc?.vyskaStatnejPomoci?.sumaEur // Alebo bola zadefinovana suma
  return result ? 'true' : 'false'
})

const radiosModelValue = ref<RadiosValuesType>(shouldBeExpanded.value)

watch(shouldBeExpanded, (value) => {
  if (value === 'true') {
    radiosModelValue.value = value
  }
})

/**
 * Ak sa náhodou stane, že kontext formulara sa načíta neskôr ako UI formulara,
 * tak sa stane, že do Vee-Validate sa zapíšu errory, ale používateľ má zvolenú možnosť FALSE (Nie)
 * a tým pádom nevidí, že nejaké errory sú a nevie ísť na daľší krok.
 * Tento watcher premaže `statnaPomoc`
 * Watcher sa spustí len jedenkrát ak zbehne podmienka.
 */
watch(
  () => formValues.value?.projekt?.financovanie?.statnaPomoc,
  () => {
    if (
      !formValues.value?.projekt?.financovanie?.statnaPomoc?.schemyStatnejPomoci &&
      !formValues.value?.projekt?.financovanie?.statnaPomoc?.vyskaStatnejPomoci
        ?.sumaEur &&
      radiosModelValue.value === 'false'
    ) {
      setStatnaPomoc(null)
    }
  },
  { flush: 'post' }
)

async function handleCheckbox(index: number, value: string, checked: boolean) {
  const checkboxValue = schemyStatnejPomociData.find(
    (schema: any) => schema.kodZdroj === value
  )

  const schemaIndex = fields.value?.findIndex(
    (field) => field.value?.kodZdroj === checkboxValue?.kodZdroj
  )

  if (checked) {
    push(checkboxValue)
  } else {
    remove(schemaIndex)

    if (fields.value?.length === 0) {
      replace([])
    }
  }
}
</script>

<template>
  <div v-if="schemyStatnejPomociData.length > 0">
    <IDSKRadios
      :label="t('zoppm.step11.part2.question')"
      :options="RADIOS_OPTIONS"
      :model-value="radiosModelValue"
      radios-wrapper-class="mb-2"
      @update:model-value="
        (value) => {
          if (value === 'false') {
            setStatnaPomoc(null)
          } else {
            replace([])
          }
        }
      "
    >
      <template #1-hidden-content>
        <IDSKCheckboxes
          id="zoppm-step11-part2-checkboxes"
          :label="t('zoppm.step11.part2.checkboxesLabel')"
          :options="schemyStatnejPomociCheckboxes"
          :model-value="checkboxValues"
          :error-message="errorMessage"
          @clicked-checkbox="handleCheckbox"
        >
        </IDSKCheckboxes>

        <ValidationWrapper
          v-slot="slotProps"
          name="projekt.financovanie.statnaPomoc.vyskaStatnejPomoci.sumaEur"
        >
          <IDSKInput
            v-bind="slotProps"
            type="number"
            :label="t('zoppm.step11.part2.sumInputLabel')"
            @focus="sumaHasFocus = true"
            @blur="sumaHasFocus = false"
          />
        </ValidationWrapper>
      </template>
    </IDSKRadios>

    <IDSKDivider class="mt-8 mb-8" />
  </div>
</template>

<style lang="scss"></style>
